import React, { useEffect, useRef, useState } from 'react'
import './player-records-list.styles.scss';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import TransactionView from '@/components/SHARED/transaction-view/transaction-view.component';
import PlayerNameIcon from '@/components/PLAYER/player-blocks/components/status-elements/player-name-icon.component';
import PlayerRecordItem from './player-record-item.component';
import { getActivityRecordsQuery, getGameActivityFromDb } from '@/utils/getDataFromDb';
import { gameActivityComplete } from '@/components/PREVIEW/previewGameActivity';

const PAGE_SIZE = 10;

/**
 * Shared list of a single player's activity records of one type
 * (loot keys, approved challenges, completed quests, momentum cards).
 * Renders one row per record with a button that opens the transaction view.
 * Records are pulled newest first, PAGE_SIZE at a time, and the next page is
 * only queried when the player clicks "Load More".
 * Used on both the manager side (/manage/.../players/...) and the player
 * side (/play/...), as well as in both preview modes.
 */
const PlayerRecordsList = ({
  gameData,
  playerData,
  gameElements,
  activityType,
  title,
  summaryLabel,
  summaryIcon,
  summaryCount,
  emptyText
}) => {

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activityToDisplay, setActivityToDisplay] = useState({});
  const [noMore, setNoMore] = useState(false);
  const activityRef = useRef({});
  const previewRef = useRef(null);
  const loadMoreTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000)

    return () => {
      if (timer) clearTimeout(timer);
      if (loadMoreTimer.current) clearTimeout(loadMoreTimer.current);
    }
  }, [])

  useEffect(() => {
    if (!gameData?.gameId || !playerData?.playerId || !activityType) return;
    activityRef.current = {};
    previewRef.current = null;
    setActivityToDisplay({});
    setNoMore(false);
    setLoading(true);
    getRecords();
  }, [gameData?.gameId, playerData?.playerId, activityType])

  /**
   * Loads one page of records. Without `next` it starts over at the newest
   * record; with `next` it continues after the oldest record already loaded.
   * @param {boolean} next - true when the player asked for another page
   */
  function getRecords(next) {
    if (location.pathname.includes('/preview/')) {
      getPreviewRecords(next);
      return;
    }
    const queryObject = {
      'gameId': gameData.gameId,
      'path': 'activityRecords',
      'filter': [
        { 'key': 'playerId', 'value': playerData.playerId },
        { 'key': 'type', 'value': activityType },
      ],
      'limit': PAGE_SIZE,
      'once': true,
      'callback': callback
    }
    if (next) {
      const loaded = sortRecords(activityRef.current);
      queryObject.lastEntry = loaded[loaded.length - 1];
    }
    getActivityRecordsQuery(queryObject);

    function callback(data) {
      activityRef.current = next
        ? { ...activityRef.current, ...data }
        : { ...data };
      setNoMore(Object.keys(data).length < PAGE_SIZE);
      setActivityToDisplay({ ...activityRef.current });
      setLoading(false);
      stopLoadingMore();
    }
  }

  // preview mode has no database to page through, so the canned activity is
  // loaded once and then revealed a page at a time
  function getPreviewRecords(next) {
    if (!previewRef.current) {
      previewRef.current = Object.values(gameActivityComplete)
        .filter(a => a.type === activityType && a.playerId === playerData.playerId)
        .sort((a, b) => a.ts < b.ts ? 1 : -1);
    }
    const start = next ? Object.keys(activityRef.current).length : 0;
    const page = previewRef.current.slice(start, start + PAGE_SIZE);
    const pageObj = {};
    page.forEach(a => pageObj[a.id] = a);
    activityRef.current = next
      ? { ...activityRef.current, ...pageObj }
      : { ...pageObj };
    setNoMore(Object.keys(activityRef.current).length >= previewRef.current.length);
    setActivityToDisplay({ ...activityRef.current });
    setLoading(false);
    stopLoadingMore();
  }

  function nextPage() {
    if (noMore || loadingMore) return;
    setLoadingMore(true);
    // the query helper swallows its own errors, so the spinner is released
    // on a timer if the next page never arrives
    if (loadMoreTimer.current) clearTimeout(loadMoreTimer.current);
    loadMoreTimer.current = setTimeout(() => setLoadingMore(false), 5000);
    getRecords(true);
  }

  function stopLoadingMore() {
    if (loadMoreTimer.current) clearTimeout(loadMoreTimer.current);
    loadMoreTimer.current = null;
    setLoadingMore(false);
  }

  function sortRecords(records) {
    return Object.values(records).sort((a, b) => a.ts < b.ts ? 1 : -1);
  }

  const recordList = sortRecords(activityToDisplay);
  const count = (summaryCount !== null && summaryCount !== undefined)
    ? summaryCount
    : recordList.length;

  return (
    <div className='player-records-list'>
      <Routes>
        <Route path='' element={
          <div className='g-card list-card'>
            <BackButton cancel={() => navigate(-1)} />
            {
              (playerData) &&
              <div>
                <div className='card-title split'>
                  {title}
                  {
                    (location.pathname.includes('/manage/')) &&
                    <PlayerNameIcon player={playerData} />
                  }
                </div>
                <div className='g-space-1'></div>
                <hr className='no-margin' />
                <div className='g-space-0-5'></div>
                <div className='summary meta'>
                  <div className='info'>
                    {summaryLabel}:
                    {
                      (summaryIcon) &&
                      <ImageContainer src={summaryIcon} className='summary-icon' />
                    }
                    <b>{count.toLocaleString()}</b>
                  </div>
                </div>
              </div>
            }
            {
              (loading)
                ?
                <div className='center-col'><ThreeDotsLoader /></div>
                :
                (recordList.length > 0)
                  ?
                  <>
                    <div className='record-list'>
                      {
                        recordList.map(a => (
                          <PlayerRecordItem
                            key={a.id ? a.id : a.ts}
                            record={a}
                            gameElements={gameElements}
                            viewTransaction={record => navigate(record.path)} />
                        ))
                      }
                    </div>
                    <div className='center-col load-more'>
                      {
                        (loadingMore)
                          ?
                          <ThreeDotsLoader />
                          :
                          <button
                            className={`g-button small-btn ${noMore ? 'disabled' : ''}`}
                            onClick={() => nextPage()}>
                            {noMore ? 'No More Records' : 'Load More...'}
                          </button>
                      }
                    </div>
                  </>
                  :
                  <h4 className='center-col'>
                    {emptyText}
                  </h4>
            }
          </div>
        }></Route>
        <Route path=':transPath/*' element={
          (gameData && playerData)
            ?
            <RecordTransactionView
              gameData={gameData}
              playerData={playerData}
              gameElements={gameElements}
              activityToDisplay={activityToDisplay} />
            :
            <div></div>
        }></Route>
      </Routes>
      <div className='g-space-2'></div>
    </div>
  )
}

/**
 * Transaction view for a single record. The record is normally already in the
 * loaded pages, but a transaction URL can be opened directly (refresh, shared
 * link) while its page has not been loaded yet, so it is fetched by path when
 * it is missing.
 */
const RecordTransactionView = ({
  gameData,
  playerData,
  gameElements,
  activityToDisplay
}) => {

  const [missingRecord, setMissingRecord] = useState({});
  const [checked, setChecked] = useState(false);
  const { transPath } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const records = { ...activityToDisplay, ...missingRecord };
  const found = Object.values(records).some(a => a.path === transPath);

  useEffect(() => {
    if (!transPath || !gameData?.gameId || !playerData?.playerId) return;
    if (found) return;
    if (location.pathname.includes('/preview/')) {
      const record = Object.values(gameActivityComplete)
        .find(a => a.path === transPath && a.playerId === playerData.playerId);
      if (record) setMissingRecord({ [record.id]: record });
      setChecked(true);
      return;
    }
    getGameActivityFromDb({
      'gameId': gameData.gameId,
      'queryArr': [
        { 'key': 'playerId', 'condition': '==', 'value': playerData.playerId },
        { 'key': 'path', 'condition': '==', 'value': transPath },
      ],
      'once': true,
      'callback': callback
    })
    function callback(data) {
      setMissingRecord({ ...data });
      setChecked(true);
    }
  }, [transPath, found])

  // TransactionView sends the player back a page when it cannot find the
  // record, so it is only mounted once the record is in hand
  return (
    <div className='g-card transaction-card'>
      {
        (found)
          ?
          <TransactionView
            gameElements={gameElements}
            activityToDisplay={records}
            playerList={{ [playerData.playerId]: playerData }}
            gameManagers={[]}
            hidePlayer={true} />
          :
          (checked)
            ?
            <div className='center-col'>
              <BackButton cancel={() => navigate(-1)} />
              <div className='g-space-1'></div>
              <h4>That transaction could not be found.</h4>
            </div>
            :
            <div className='center-col'><ThreeDotsLoader /></div>
      }
    </div>
  )
}

export default PlayerRecordsList
